
import React from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Plus, ArrowLeft } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import ContentItemsList from "@/components/admin/content/ContentItemsList";
import ContentItemDialog from "@/components/admin/content/ContentItemDialog";
import DeleteConfirmationDialog from "@/components/admin/content/DeleteConfirmationDialog";
import { useLessonContent } from "@/hooks/useLessonContent";

const LessonContentPage: React.FC = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  
  const {
    lesson,
    contentItems,
    isLoading,
    isCreating,
    isEditing,
    isDeleteDialogOpen,
    currentContentItem,
    setCurrentContentItem,
    setIsCreating,
    setIsEditing,
    setIsDeleteDialogOpen,
    handleCreateContent,
    handleEditContent,
    handleDeleteContent,
    handleMoveContent,
    openEditDialog,
    openDeleteDialog,
    openCreateDialog,
  } = useLessonContent(lessonId);

  if (!lessonId) {
    return (
      <AdminLayout>
        <div className="container py-8">
          <h1>Lesson not found</h1>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container py-8">
        <div className="mb-6">
          {lesson && (
            <Link 
              to={`/admin/courses/modules/${lesson.module_id}/${lesson.module_id}/lessons`}
              className="flex items-center text-techne-purple hover:underline mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Module Lessons
            </Link>
          )}
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">{lesson?.title || "Lesson Content"}</h1>
              {lesson?.description && (
                <p className="text-gray-600 mt-1">{lesson.description}</p>
              )}
              {lesson?.module_title && (
                <p className="text-sm text-gray-500 mt-1">Module: {lesson.module_title}</p>
              )}
            </div>
            
            <Button onClick={openCreateDialog}>
              <Plus className="mr-2 h-4 w-4" /> Add Content
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Content Items</CardTitle>
            <CardDescription>
              Manage content items for this lesson. Upload PDFs and other content for your learners.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ContentItemsList
              contentItems={contentItems}
              onEditClick={openEditDialog}
              onDeleteClick={openDeleteDialog}
              onMoveClick={handleMoveContent}
              isLoading={isLoading}
            />
          </CardContent>
        </Card>

        {/* Content Item Dialog (for both create and edit) */}
        <ContentItemDialog
          isOpen={isCreating || isEditing}
          isCreating={isCreating}
          lessonId={lessonId}
          currentContentItem={currentContentItem}
          setCurrentContentItem={setCurrentContentItem}
          onOpenChange={(open) => {
            if (!open) {
              isCreating ? setIsCreating(false) : setIsEditing(false);
            }
          }}
          onSave={isCreating ? handleCreateContent : handleEditContent}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          isOpen={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          onConfirmDelete={handleDeleteContent}
        />
      </div>
    </AdminLayout>
  );
};

export default LessonContentPage;
